import { EMPTY, Observable, of as observableOf } from "rxjs";

import { map, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Fiat, Field, PayoutAccount, PayoutProvider } from "@models";

import { InfoService } from "@core-services/info.service";

import { MasterdataService } from "@core-services/masterdata.service";
import { Service } from "./base-service";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { Helper } from "@helpers/functions";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { DestructorService } from "@core-services/destructor.service";
import { TranslationService } from "@core-services/translation.service";

const name = ServiceNames.PAY_ACCOUNT;

@Injectable({
	providedIn: "root"
})
export class PayAccountService extends Service<PayoutAccount> {
	public name: string = ServiceNames.PAY_ACCOUNT;

	public constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		private apiCallService: ApicallService,
		private masterdataService: MasterdataService,
		private translationService: TranslationService,
		private infoService: InfoService
	) {
		super(initService, destructor, name);
	}

	public deletePayoutAccount(account: PayoutAccount): Observable<PayoutAccount[]> {
		if (account == null) {
			return EMPTY;
		}
		return this.apiCallService
			.deleteRequest("payoutaccount/" + account.getId(), new ApicallOption(PayoutAccount))
			.pipe(
				tap(
					(ans: PayoutAccount[]) => {
						this.upsert(ans[0]);
						this.infoService.setSuccess(
							this.translationService.translate("info_text_payout_account_successfully_deleted")
						);
					},
					(error) => Helper.handleError(error)
				)
			);
	}

	public getPrimaryFieldForAccount(payoutAccount: PayoutAccount): Field {
		const provider = this.masterdataService.getPayoutProviderById(payoutAccount.getPayoutProviderId());
		const primaryField = provider.fields.find((f: Field) => f.isPrimary());

		return !primaryField
			? null
			: payoutAccount.getFields().find((f: Field) => f.getName() === primaryField.getName());
	}

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public upsertCall(account: PayoutAccount): Observable<any> {
		const a = new ApicallOption(PayoutAccount, account);

		let call = this.apiCallService.post("payoutaccounts", a);

		const edit = !account.isNew();
		if (edit) {
			call = this.apiCallService.put("payoutaccount/" + account.getId(), a);
		}

		return call.pipe(
			map((ans) => {
				this.upsert(ans[0]);
				return ans[0];
			})
		);
	}

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public getAccountExtraDetails(id: string): Observable<any> {
		if (this.getItemById(id)) {
			return observableOf(this.getItemById(id));
		}

		return this.apiCallService.get("payoutaccounts/" + id, new ApicallOption(PayoutAccount)).pipe(
			map((res) => {
				if (res[0]) {
					this.upsert(res[0]);
					return res[0];
				}
				return res;
			})
		);
	}

	/**
	 * We want to grad ALL accounts for one provider (BP payments atm, the other are restricted in numbers)
	 */
	public getListForProvider(p: PayoutProvider, fiat: Fiat): Observable<PayoutAccount[]> {
		// Have one function that checks instead of crowded ifs
		const callback: (p: PayoutAccount) => boolean = (item) =>
			item.getPayoutProviderId() === p.getId() && item.getFiatId() === fiat.getId() && !item.isDeleted();

		// If we have at least one item: we already fetched
		if (this.list.find(callback)) {
			return this.getList().pipe(map((l) => l.filter(callback)));
		}

		// We fetch the accounts
		return this.apiCallService
			.get("payoutproviders/" + p.getId() + "/payoutaccounts", new ApicallOption(PayoutAccount))
			.pipe(
				map((list) => {
					list.forEach((i) => {
						// Dont do upserts in case we will subribe to the list to not go wild on the emits :D
						if (!this.hasItem(i)) {
							this.addItem(i);
						}
					});
					// This shouldn't be necessary, just to make sure we return only NON deleted also
					return list.filter(callback);
				})
			);
	}
}
